import { ApiCustomScoringRoutePointsConfig, ApiMediaInline } from ".";
import { ISOTimestamp } from "./common";
import { AbilityCategory, ApiUserInline } from "./profiles";

export type ClimbType = "BOULDER" | "SPORT";

export type RouteDisplayMode = "NUMBER" | "GRADE" | "NONE";

export type ApiCircuit = {
  uuid: string;
  name: string;
  description: string | null;
  colors: string[];
  creationTime: ISOTimestamp;
  startTime: ISOTimestamp;
  endTime: ISOTimestamp | null;
  scheduledEndTime: ISOTimestamp | null;
  creator: ApiUserInline | null;
  setters: ApiUserInline[];
  venue: string;
  gradeSystem: string;
  minGradeInternal: number | null;
  minGradeValue: string | null;
  maxGradeInternal: number | null;
  maxGradeValue: string | null;
  routeDisplay: RouteDisplayMode;
  gradesHidden: boolean;
  gradesEstimated: boolean;
  media: ApiMediaInline[];
  previousVersion: string | null;
  isNamedByGradeRange: boolean;
};

export type ApiLine = {
  uuid: string;
  name: string;
  isActive: boolean;
  map: string;
  mapPosition: ApiMapCoords;
  quickdrawCount: number | null;
  height: number;
  autoBelay: boolean;
  venue: string;
  zone?: ApiRouteZone | null;
  routesCount?: number;
  lastSetTime?: ISOTimestamp | null;
  canLead: boolean;
  canToprope: boolean;
};

export const climbStyleValues = [
  "SLAB",
  "OVERHANG",
  "VERT",
  "ROOF",
  "ARETE",
  "GROOVE",
  "CHIMNEY",
  "STEP",
  "BARREL",
  "TRAVERSE",
  "TOPOUT",
  "WILD",
] as const;

export type ClimbStyle = (typeof climbStyleValues)[number];

export const climbSkillValues = [
  "DYNAMIC",
  "SUSTAINED",
  "POWER",
  "TECHNICAL",
  "REACHY",
  "FINGERY",
  "SHORT_REACH",
] as const;

export type ClimbSkill = (typeof climbSkillValues)[number];

export const holdTypeValues = [
  "JUG",
  "CRACK",
  "CRIMPS",
  "PINCH",
  "SLOPERS",
  "POCKET",
  "FEATURES",
  "UNDERCUT",
  "COMPRESSION",
  "SIDEPULL",
  "VOLUME",
] as const;

export type HoldType = (typeof holdTypeValues)[number];

export type RouteDifficulty = -1 | 0 | 1 | 2;

export type ApiRouteCircuit = {
  uuid: string;
  name: string | null;
  number: number | null;
  difficulty: RouteDifficulty | null;
  routeDisplay: RouteDisplayMode;
  colors?: string[];
};

export type ApiRouteLine = {
  uuid: string;
  name: string;
  isActive: boolean;
  canLead: boolean;
  canToprope: boolean;
};

export type ApiRouteZone = {
  uuid: string;
  name: string;
  color: string;
};

export type ApiZone = {
  uuid: string;
  name: string;
  color: string | null;
  routesCount: number;
  climbType?: ClimbType;
};

export type ApiCreateZonePayload = {
  name: string;
  color?: string | null;
  climbType: ClimbType;
};

export type ApiUpdateZonePayload = Omit<
  Partial<ApiCreateZonePayload>,
  "climbType"
>;

// A label inside a group has no `isPublic`, because it takes its visibility
// from the group. `routeCount` is the number of live routes with the label,
// and only the config tree has it.
export type ApiGroupedRouteLabel = {
  uuid: string;
  name: string;
  orderIndex: number;
  isActive: boolean;
  routeCount: number;
};

export type ApiRootRouteLabel = ApiGroupedRouteLabel & {
  isPublic: boolean;
};

export type ApiRouteLabelGroupInfo = {
  uuid: string;
  name: string;
  orderIndex: number;
  isPublic: boolean;
  isActive: boolean;
};

// `routeCount` is the number of live routes with any of the group's labels,
// archived ones included.
export type ApiRouteLabelGroup = ApiRouteLabelGroupInfo & {
  routeCount: number;
  labels: ApiGroupedRouteLabel[];
};

export type ApiRouteLabelConfig = {
  groups: ApiRouteLabelGroup[];
  labels: ApiRootRouteLabel[];
};

// What creating or editing a single label returns: `group` is the group's
// uuid, and `isPublic` is only there for root labels.
export type ApiRouteLabel = Omit<ApiGroupedRouteLabel, "routeCount"> & {
  group: string | null;
  isPublic?: boolean;
};

// A label as it appears on a route, with just enough of its group to show a
// heading. Routes only include the labels the requester may see.
export type ApiRouteLabelInline = {
  uuid: string;
  name: string;
  group: { uuid: string; name: string } | null;
};

export type ApiCreateRouteLabelGroupPayload = {
  name: string;
  isPublic: boolean;
};

export type ApiUpdateRouteLabelGroupPayload = Partial<
  ApiCreateRouteLabelGroupPayload & { isActive: boolean; orderIndex: number }
>;

export type ApiCreateRouteLabelPayload = {
  name: string;
  group?: string | null;
  isPublic?: boolean;
};

// A label's group is fixed once it's created, so it can't be sent here.
export type ApiUpdateRouteLabelPayload = Partial<{
  name: string;
  isPublic: boolean;
  isActive: boolean;
  orderIndex: number;
}>;

export type ApiBulkRouteLabelGroupPatch = ApiUpdateRouteLabelGroupPayload & {
  uuid: string;
};

export type ApiBulkRouteLabelPatch = ApiUpdateRouteLabelPayload & {
  uuid: string;
};

export type ApiCreateLinePayload = {
  name: string;
  height: number;
  quickdrawCount: number;
  mapPosition: ApiMapCoords;
  description?: string;
  zone?: string | null;
  autoBelay?: boolean;
  venue: string;
};

export type ApiUpdateLinePayload = Partial<ApiCreateLinePayload>;

export type ApiRouteCompetitionRouteGroup = {
  uuid: string;

  /**
   * The UUID of the competition that the route group belongs to
   */
  competition: string;

  /**
   * The name of the route group
   */
  name: string;

  /**
   * The UUIDs of the categories that the route group is available to
   */
  categories: string[];

  competitionName: string;
  leagueName: string | null;
};

export type ApiMapCoords = {
  x: number;
  y: number;
};

/**
 * Information about the current user's attempts on a route
 */
export type ApiRouteAttemptsInfo = {
  flash: boolean;
  count: number;
  successes: number;
  segmentsCount: number;
  topropeSuccesses: number;
  leadSuccesses: number;
  lastAttemptTime: ISOTimestamp | null;
  lastSuccessTime: ISOTimestamp | null;
  firstSuccessTime: ISOTimestamp | null;
  failedAttemptsBeforeFirstSuccess: number | null;
};

type ApiRouteFeedback = {
  averageStarRating: number | null;

  /**
   * The ranking of the route based on its average and count of star ratings.
   * Currently this is the same as the average rating, but it may change in the future.
   */
  starRatingsRanking: number | null;

  counts: {
    likes: number;
    dislikes: number;
    difficulty: {
      "-1": number;
      "0": number;
      "1": number;
    };
    reactions: {
      MASTERPIECE: number;
      DELIGHTFUL: number;
      MINDBLOWING: number;
      SCARY: number;
      BORING: number;
      null: number;
    };
    starRatingsCount: number;
  };

  me: {
    liked: boolean;
    difficulty: number | null;
    reaction:
      | "MASTERPIECE"
      | "DELIGHTFUL"
      | "MINDBLOWING"
      | "SCARY"
      | "BORING"
      | null;
    starRating: number | null;
  };
};

type ApiRouteBase = {
  uuid: string;

  name: string | null;

  /**
   * Not really used at the moment, but added for completeness
   */
  description: string | null;

  /**
   * The type of climbing: BOULDER or SPORT
   */
  climbType: ClimbType;

  /**
   * Hex colors separated with the character ':'
   */
  colors: string | null;

  /**
   * The date when the route was created
   */
  creationTime: ISOTimestamp;

  /**
   * The date when the route becomes available to climb
   */
  startTime: ISOTimestamp | null;

  /**
   * The date when the route is stripped
   */
  endTime: ISOTimestamp | null;

  /**
   * The scheduled date for stripping the route. May differ from the actual endTime value.
   */
  scheduledEndTime: ISOTimestamp | null;

  /**
   * For boulder routes, the circuit that the route belongs to.
   */
  circuit: ApiRouteCircuit | null;

  /**
   * The uuid of the route's venue
   */
  venue: string;

  /**
   * For sport routes, the line that the route is on.
   */
  startLine: ApiRouteLine | null;

  /**
   * The zone within the venue that the route is located within.
   */
  zone: ApiRouteZone | null;

  /**
   * The grade system used for the route, e.g. HUECO or FONT
   */
  gradeSystem: string;

  /**
   * The user-visible grade value of the route. It can be null.
   */
  gradeValue: string | null;

  /**
   * The internal IRCRA grade value of the route. It can be null.
   */
  gradeInternal: number | null;

  /**
   * The grade value for display in map pins and other space-limited contexts.
   */
  pinGradeValue: string | null;

  /**
   * The number of segments in the route - this has several meanings depending on the context
   */
  segmentsCount: number | null;

  /**
   * A mapping of route group UUIDs to display names and numbers
   */
  displayRouteGroupMapping?: Record<string, { name: string; number: number }>;

  /**
   * The time at which the route was restored from the archive
   */
  restoreTime: ISOTimestamp | null;

  competitionRouteGroups: ApiRouteCompetitionRouteGroup[];
};

export type ApiRouteInline = ApiRouteBase & {
  attemptsCount: number;
  successesCount: number;
  flashesCount: number;
  zonesCount: number;
  zone2Count: number;
  climbersCount: number;
};

export type ApiRouteDetail = ApiRouteBase & {
  /**
   * The user that created the route
   */
  creator: ApiUserInline | null;

  /**
   * The setters for this route
   */
  setters: ApiUserInline[];

  /**
   * The UUID of the map that the route is on
   */
  map: string | null;

  /**
   * The position of the route on the map
   */
  mapPosition: ApiMapCoords | null;

  /**
   * The height of the route in integer centimeters
   */
  height: number | null;

  /**
   * The number of quickdraws on the route (sport-only)
   */
  quickdrawCount: number | null;

  /**
   * Is the route an auto-belay route?
   */
  autoBelay: boolean;

  /**
   * Can the route be lead-climbed?
   */
  canLead: boolean;

  /**
   * Is the grade value for this route an estimated one?
   */
  gradeEstimated: boolean;

  /**
   * The climbing styles used in the route
   */
  styles: ClimbStyle[];

  /**
   * The climbing skills used in the route
   */
  skills: ClimbSkill[];

  /**
   * The types of holds used in the route
   */
  holds: HoldType[];

  /**
   * Information about the current user's attempts on the route
   */
  attempts: ApiRouteAttemptsInfo;

  betaCounts: {
    count: number;
    pendingCount: number;
  };

  circuitNumber: number | null;

  /**
   * The ability category of the route, based on its grade
   */
  abilityCategory: AbilityCategory | null;

  feedback: ApiRouteFeedback;

  competitionRouteGroups: ApiRouteCompetitionRouteGroup[];

  tag: {
    id: string;
    color: string;
    number: number | null;
    climbType: ClimbType;
  };

  displayRouteGroupMapping: Record<string, { name: string; number: number }>;

  attemptsCount?: number;
  successesCount?: number;
  flashesCount?: number;
  zonesCount?: number;
  zone2Count?: number;
  climbersCount?: number;

  /**
   * The route's labels, grouped ones first
   */
  labels: ApiRouteLabelInline[];

  /**
   * How many climber comments on this route are waiting in the staff inbox.
   * Only present for users with a role at the route's venue.
   */
  pendingCommentsCount?: number;
};

export type ApiRouteCommentOutcome = "HANDLED" | "IGNORED";

/**
 * A climber's private comment on a route, as venue staff see it. It's in the
 * inbox while `archiveTime` is null.
 */
export type ApiRouteComment = {
  uuid: string;
  text: string;
  timestamp: ISOTimestamp;
  route: ApiRouteBase;
  user: ApiUserInline;
  outcome: ApiRouteCommentOutcome | null;
  archivedBy: ApiUserInline | null;
  archiveTime: ISOTimestamp | null;
};

/**
 * What a climber gets back after submitting a comment.
 */
export type ApiSubmittedRouteComment = {
  uuid: string;
  text: string;
  timestamp: ISOTimestamp;
};

export type ApiCustomGradeSystemInfo = {
  pinValues: Record<string, string>;
  label: string;
};

export type ApiGradesResponse = {
  customSystems: Record<string, ApiCustomGradeSystemInfo>;
  mappings: Record<string, Record<string, number>>;
} & Record<string, string[]>;

export type ApiGradesResponseRaw = {
  custom_systems: Record<
    string,
    { pin_values: Record<string, string>; label: string }
  >;
  mappings: Record<string, Record<string, number>>;
} & Record<string, string[]>;

export type ApiRoutePayload = {
  name?: string | null;
  description?: string | null;
  circuit?: {
    uuid?: string | null;
    difficulty?: number | null;
    number?: number | null;
  } | null;
  climbType: ClimbType;
  gradeSystem: string | null;
  gradeValue: string | null;
  holds?: string[];
  skills?: string[];
  styles?: string[];
  colors?: string | null;
  quickdrawCount?: number | null;
  height?: number | null;
  startTime?: ISOTimestamp | null;
  scheduledEndTime?: ISOTimestamp | null;
  endTime?: ISOTimestamp | null;
  segmentsCount?: number | null;
  nfcId?: string | null;
  tag?: string | null;
  venue: string;
  map: string | null;
  mapPosition?: {
    x: number;
    y: number;
  } | null;
  setters?: string[];
  /** Active label UUIDs. An empty list clears active labels; omission preserves them. */
  labels?: string[];
  startLine?: string | null;
  zone?: string | null;
  autoBelay?: boolean | null;
  competitionRouteGroups?: string[];
  displayRouteGroupMapping?: Record<
    string,
    { name: string | null; number: number | null }
  >;
  circuitNumber?: number | null;
};

export type ApiBulkRoutePatch = {
  uuid: string;
  circuit?: string | null;
} & Partial<
  Pick<
    ApiRoutePayload,
    | "holds"
    | "skills"
    | "styles"
    | "colors"
    | "quickdrawCount"
    | "height"
    | "startTime"
    | "scheduledEndTime"
    | "endTime"
    | "segmentsCount"
    | "setters"
    | "labels"
    | "startLine"
    | "zone"
    | "autoBelay"
    | "competitionRouteGroups"
  >
>;

export type ApiRouteStats = {
  uuid: string;
  attemptsCount: number;
  successesCount: number;
  flashesCount: number;
};

export type ApiRouteAttempt = {
  uuid: string;
  segmentsCount: number | null;
  sportType: "TOPROPE" | "LEAD" | null;
  success: boolean;
  timestamp: string;
  user?: string;
  usedLastHold?: boolean;
  duration?: number;
  notes?: string;
  judge?: string | null;
  score?: number | null;
};

export type ApiRouteCustomScoringConfig = {
  config: ApiCustomScoringRoutePointsConfig;
};

export type ApiCompetitionRouteCustomScoringConfig = {
  route: string;
} & ApiRouteCustomScoringConfig;

export type ApiRoutesTarget = {
  uuid: string;
  zone: string | null;
  ascentStyle: "TOPROPE" | "LEAD" | "AUTO_BELAY";
  targetCount: number;
  currentCount?: number;
  gradeValue: string | null;
  gradeInternal: number | null;
};

export type ApiTargetWithCount = ApiRoutesTarget & {
  currentCount: number;
};

export type ApiRoutesTargetConfig = {
  mode: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE";
  tolerance: number;
  warningThreshold: number;
};

export type ApiRoutesTargetsResponse = {
  targets: ApiTargetWithCount[];
  config: ApiRoutesTargetConfig;
};

export type ApiBulkRoutesTargetPatch = {
  uuid: string;
  zone?: string | null;
  ascentStyle?: "TOPROPE" | "LEAD" | "AUTO_BELAY";
  targetCount?: number;
};
